import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import PackageDetailsForm from "@/components/travel/PackageDetailsForm";
import { TravelBnplProviderSelection } from "@/components/payments/BNPLProviderSelection";
import PaymentConfirmation from "@/components/payments/PaymentConfirmation";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, MapPin, Plane, Shield, Lock, BadgeCheck, ChevronDown, HelpCircle } from "lucide-react";
import { calcMonthly } from "@/lib/providers/index";

// Import the same package data from Travel page
import emiratesLogo from "@/assets/airlines/emirates-logo.png";
import airfranceLogo from "@/assets/airlines/airfrance-logo.png";
import jalLogo from "@/assets/airlines/jal-logo.png";
import dubaiMarinaImg from "@/assets/stay-dubai-marina.jpg";
import santoriniImg from "@/assets/stay-santorini.jpg";
import tokyoImg from "@/assets/stay-tokyo.jpg";
import londonImg from "@/assets/stay-london.jpg";
import parisImg from "@/assets/stay-paris.jpg";
import bangkokImg from "@/assets/stay-bangkok.jpg";
import istanbulImg from "@/assets/stay-istanbul.jpg";
import singaporeImg from "@/assets/stay-singapore.jpg";
import romeImg from "@/assets/stay-rome.jpg";
import maldivesImg from "@/assets/stay-maldives.jpg";
import newyorkImg from "@/assets/stay-newyork.jpg";
import niceImg from "@/assets/stay-nice.jpg";

// Provider logos
import tabbyLogo from "@/assets/logos/tabby-logo.png";
import tamaraLogo from "@/assets/logos/tamara-logo.png";
import cashewLogo from "@/assets/logos/cashew-logo.png";

type BookingStep = "details" | "provider" | "confirmation";

interface BookingData {
  packageId: string;
  startDate: string;
  endDate: string;
  travelers: number;
  rooms: number;
  specialRequests?: string;
  selectedProvider?: string;
}

// Travel packages data - same as in Travel.tsx
const travelPackages = [
  {
    id: "1",
    destination: "Dubai Experience",
    flight: { route: "Dubai → London → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Marina View Hotel", nights: 4, image: dubaiMarinaImg },
    totalPrice: 4200,
    savings: 400
  },
  {
    id: "2", 
    destination: "Greek Island Getaway",
    flight: { route: "Dubai → Athens → Dubai", carrier: "Air France", logo: airfranceLogo },
    stay: { name: "Santorini Luxury Suite", nights: 5, image: santoriniImg },
    totalPrice: 6000,
    savings: 600
  },
  {
    id: "3",
    destination: "Tokyo Adventure", 
    flight: { route: "Dubai → Tokyo → Dubai", carrier: "JAL", logo: jalLogo },
    stay: { name: "Tokyo Business Hotel", nights: 4, image: tokyoImg },
    totalPrice: 4400,
    savings: 350
  },
  {
    id: "4",
    destination: "London City Break",
    flight: { route: "Dubai → London → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "London Central Hotel", nights: 3, image: londonImg },
    totalPrice: 3800,
    savings: 300
  },
  {
    id: "5",
    destination: "Paris Romance",
    flight: { route: "Dubai → Paris → Dubai", carrier: "Air France", logo: airfranceLogo },
    stay: { name: "Champs-Élysées Suite", nights: 4, image: parisImg },
    totalPrice: 5200,
    savings: 520
  },
  {
    id: "6",
    destination: "Mediterranean Escape",
    flight: { route: "Dubai → Nice → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Riviera Resort", nights: 5, image: niceImg },
    totalPrice: 5800,
    savings: 450
  },
  {
    id: "7",
    destination: "New York Explorer",
    flight: { route: "Dubai → New York → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Manhattan Plaza Hotel", nights: 6, image: newyorkImg },
    totalPrice: 7200,
    savings: 800
  },
  {
    id: "8",
    destination: "Bangkok Discovery",
    flight: { route: "Dubai → Bangkok → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Riverside Bangkok Hotel", nights: 4, image: bangkokImg },
    totalPrice: 3600,
    savings: 280
  },
  {
    id: "9",
    destination: "Istanbul Cultural Tour",
    flight: { route: "Dubai → Istanbul → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Bosphorus View Hotel", nights: 4, image: istanbulImg },
    totalPrice: 3400,
    savings: 250
  },
  {
    id: "10",
    destination: "Singapore Luxury",
    flight: { route: "Dubai → Singapore → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Marina Bay Suites", nights: 5, image: singaporeImg },
    totalPrice: 4800,
    savings: 380
  },
  {
    id: "11",
    destination: "Rome Historic Journey",
    flight: { route: "Dubai → Rome → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Colosseum View Hotel", nights: 4, image: romeImg },
    totalPrice: 4600,
    savings: 320
  },
  {
    id: "12",
    destination: "Maldives Paradise",
    flight: { route: "Dubai → Malé → Dubai", carrier: "Emirates", logo: emiratesLogo },
    stay: { name: "Overwater Villa Resort", nights: 7, image: maldivesImg },
    totalPrice: 8500,
    savings: 900
  }
];

const STEP_LABELS = ["Choose Plan", "Verify Identity", "Confirmed"];

const PROVIDER_LOGOS: Record<string, string> = {
  tabby: tabbyLogo,
  tamara: tamaraLogo,
  cashew: cashewLogo,
};

const PROVIDER_NAMES: Record<string, string> = {
  tabby: "Tabby",
  tamara: "Tamara",
  cashew: "Cashew",
};

export default function PackageBooking() {
  const { packageId } = useParams<{ packageId: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<BookingStep>("details");
  const [bookingData, setBookingData] = useState<BookingData>({
    packageId: packageId || "",
    startDate: "",
    endDate: "",
    travelers: 1,
    rooms: 1
  });

  // Find the selected package
  const selectedPackage = useMemo(() => {
    return travelPackages.find(pkg => pkg.id === packageId);
  }, [packageId]);

  const handleDetailsComplete = (data: Partial<BookingData>) => {
    setBookingData(prev => ({ ...prev, ...data }));
    setCurrentStep("provider");
  };

  const handleProviderSelection = (provider: string) => {
    setBookingData(prev => ({ ...prev, selectedProvider: provider }));
    setCurrentStep("confirmation");
  };

  const handleConfirmPayment = () => {
    console.log("Redirecting to BNPL provider for payment...");
  };

  const goBack = () => {
    if (currentStep === "provider") {
      setCurrentStep("details");
    } else if (currentStep === "confirmation") {
      setCurrentStep("provider");
    } else {
      navigate("/travel");
    }
  };

  const stepIndex = currentStep === "details" ? 0 : currentStep === "provider" ? 1 : 2;
  const monthlyPayment = selectedPackage ? calcMonthly(selectedPackage.totalPrice, 4) : 0;
  const providerName = bookingData.selectedProvider
    ? PROVIDER_NAMES[bookingData.selectedProvider] || "BNPL Provider"
    : "BNPL Provider";

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-20">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header with back button */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              className="flex items-center gap-2 font-body"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-10">
            <div className="flex items-center space-x-3">
              {STEP_LABELS.map((label, i) => (
                <div key={label} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-medium font-body transition-colors ${
                        i < stepIndex
                          ? "bg-flexaura-success text-white"
                          : i === stepIndex
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {i < stepIndex ? "✓" : i + 1}
                    </div>
                    <span
                      className={`mt-1.5 text-xs font-body font-medium ${
                        i <= stepIndex ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {i < STEP_LABELS.length - 1 && (
                    <div
                      className={`h-0.5 w-12 sm:w-20 mx-2 ${
                        i < stepIndex ? "bg-flexaura-success" : "bg-muted"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Two-column layout on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* LEFT COLUMN (60%) — Main content */}
            <div className="lg:col-span-3">
              <Card className="p-6 md:p-8">
                {currentStep === "details" && (
                  <PackageDetailsForm
                    packageId={packageId || ""}
                    selectedPackage={selectedPackage}
                    onComplete={handleDetailsComplete}
                    initialData={bookingData}
                  />
                )}
                {currentStep === "provider" && (
                  <div className="space-y-6">
                    <TravelBnplProviderSelection
                      orderId={bookingData.packageId}
                      amount={selectedPackage?.totalPrice || 0}
                      currency="AED"
                      customerName=""
                      customerEmail=""
                    />

                    {/* How does this work? collapsible */}
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm font-body font-medium text-muted-foreground hover:text-foreground transition-colors w-full">
                        <HelpCircle className="w-4 h-4" />
                        How does this work?
                        <ChevronDown className="w-4 h-4 ml-auto transition-transform [[data-state=open]_&]:rotate-180" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3 pl-6 space-y-2">
                        <p className="text-sm text-muted-foreground font-body">
                          • <strong>Choose a plan</strong> — We match you with the best BNPL provider based on your booking amount and eligibility.
                        </p>
                        <p className="text-sm text-muted-foreground font-body">
                          • <strong>Quick identity check</strong> — You'll be redirected to the provider's secure page for a brief KYC and approval (usually under 2 minutes).
                        </p>
                        <p className="text-sm text-muted-foreground font-body">
                          • <strong>Booking confirmed</strong> — Once approved, your booking is instantly confirmed and you pay in easy monthly instalments.
                        </p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Redirect explainer below CTA */}
                    <p className="text-xs text-muted-foreground font-body text-center">
                      You'll be redirected to {providerName} to complete identity verification and approval.
                    </p>
                  </div>
                )}
                {currentStep === "confirmation" && (
                  <PaymentConfirmation
                    vertical="travel"
                    bookingData={bookingData}
                    onConfirm={handleConfirmPayment}
                  />
                )}
              </Card>
            </div>

            {/* RIGHT COLUMN (40%) — Order Summary */}
            <div className="lg:col-span-2">
              <Card className="p-6 border border-border shadow-card sticky top-24">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Order Summary
                </h3>

                {selectedPackage ? (
                  <>
                    {/* Package name */}
                    <p className="font-body font-semibold text-foreground">
                      {selectedPackage.destination}
                    </p>

                    {/* Key details */}
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                        <Plane className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{selectedPackage.flight.route}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
                        <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{selectedPackage.stay.name} · {selectedPackage.stay.nights} nights</span>
                      </div>
                      {bookingData.startDate && (
                        <div className="text-sm text-muted-foreground font-body">
                          {new Date(bookingData.startDate).toLocaleDateString("en-AE", { month: "short", day: "numeric" })}
                          {bookingData.endDate && ` – ${new Date(bookingData.endDate).toLocaleDateString("en-AE", { month: "short", day: "numeric", year: "numeric" })}`}
                        </div>
                      )}
                    </div>

                    <Separator className="my-5" />

                    {/* Total amount */}
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground font-body uppercase tracking-wider mb-1">Total Amount</p>
                      <p className="text-3xl font-heading font-semibold text-foreground">
                        <span className="font-mono">AED {selectedPackage.totalPrice.toLocaleString()}</span>
                      </p>
                    </div>

                    {/* Instalment breakdown */}
                    <div className="mt-4 bg-primary/5 rounded-lg p-3 text-center">
                      <p className="text-sm font-mono text-accent font-medium">
                        4 × AED {monthlyPayment}/month
                      </p>
                      <p className="text-xs text-muted-foreground font-body mt-0.5">
                        {bookingData.selectedProvider
                          ? `with ${providerName}`
                          : "with BNPL provider"}
                      </p>
                    </div>

                    <Separator className="my-5" />

                    {/* Trust signals */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-sm text-muted-foreground font-body">
                        <Lock className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>256-bit SSL encryption</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground font-body">
                        <BadgeCheck className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>Powered by licensed UAE BNPL partners</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground font-body">
                        <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>No hidden fees</span>
                      </div>
                    </div>

                    {/* Selected provider logo */}
                    {bookingData.selectedProvider && PROVIDER_LOGOS[bookingData.selectedProvider] && (
                      <>
                        <Separator className="my-5" />
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xs text-muted-foreground font-body">Paying with</span>
                          <img
                            src={PROVIDER_LOGOS[bookingData.selectedProvider]}
                            alt={providerName}
                            className="h-6 w-auto object-contain"
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground font-body text-sm">Package ID: {packageId}</p>
                )}
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
